import { lazy, Suspense } from "react";
import { Routes, Route, Outlet, Link } from "react-router-dom";
import Login from "./components/login/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import "./App.css";
import Sidebar from "./components/sidebar/sidebar";
import { QueryProvider } from "./providers/QueryProvider";

// Import page components
import Dashboard from "./components/pages/dashboard/Dashboard";
import Activities from "./components/pages/activities/Activities";
import Countries from "./components/pages/countries/Countries";
import States from "./components/pages/states/States";
import Destinations from "./components/pages/destinations/Destinations";
import Meals from "./components/pages/meals/Meals";
import Suppliers from "./components/pages/suppliers/Suppliers";
import Users from "./components/pages/users/Users";
import Enquiry from "./components/pages/enquiry/Enquiry";
import Bookings from "./components/pages/bookings/Bookings";
import Package from "./components/pages/package/Package";
import AddPackagePage from "./components/pages/package/AddPackagePage";
import Events from "./components/pages/events/Events";
import EventCategory from "./components/pages/eventCategory/EventCategory";
import ActivityCategory from "./components/pages/activityCategory/ActivityCategory";
import AttractionCategory from "./components/pages/attractionCategory/AttractionCategory";
import Language from "./components/pages/language/language";
import Inclusions from "./components/pages/inclusions/Inclusions";
import Suitable from "./components/pages/suitable/Suitable";
import Attractions from "./components/pages/attractions/Attractions";
import SupplierActivities from "./components/pages/supplierActivities/SupplierActivities";
import Settings from "./components/pages/settings/Settings";
import Transactions from "./components/pages/transactions/Transactions";
import Rentals from "./components/pages/rentals/Rentals";
import RentalEdit from "./components/pages/rentals/RentalEdit";
import RentalManagement from "./components/pages/rentals/RentalManagement";

const AddEventPage = lazy(() =>
  import("./components/pages/events/AddEventPage")
);
const AddAttractionPage = lazy(() =>
  import("./components/pages/attractions/AddAttractionPage")
);

const PageLoader = () => (
  <div className="flex min-h-[40vh] items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

// Add this new ProtectedLayout component
const ProtectedLayout = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />
      <main className="relative z-0 min-h-screen min-w-0 transform-gpu p-4 lg:ml-64">
        <Outlet />
      </main>
    </div>
  );
};

function App() {
  return (
    <QueryProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />

          {/* Protected routes with sidebar */}
          <Route
            element={
              <ProtectedRoute>
                <ProtectedLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="activities" element={<Activities />} />
            <Route path="countries" element={<Countries />} />
            <Route path="states" element={<States />} />
            <Route path="destinations" element={<Destinations />} />
            <Route path="meals" element={<Meals />} />
            <Route path="suppliers" element={<Suppliers />} />
            <Route path="users" element={<Users />} />
            <Route path="enquiry" element={<Enquiry />} />
            <Route path="bookings" element={<Bookings />} />
            <Route path="packages" element={<Package />} />
            <Route path="packages/new" element={<AddPackagePage />} />
            <Route path="packages/:packageId/edit" element={<AddPackagePage />} />
            <Route path="rentals" element={<Rentals />} />
            <Route path="rentals/management" element={<RentalManagement />} />
            <Route path="rentals/:id" element={<RentalEdit />} />
            <Route path="events" element={<Events />} />
            <Route
              path="events/new"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AddEventPage />
                </Suspense>
              }
            />
            <Route
              path="events/:eventId/edit"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AddEventPage />
                </Suspense>
              }
            />
            <Route path="attractions" element={<Attractions />} />
            <Route
              path="attractions/new"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AddAttractionPage />
                </Suspense>
              }
            />
            <Route
              path="attractions/:attractionId/edit"
              element={
                <Suspense fallback={<PageLoader />}>
                  <AddAttractionPage />
                </Suspense>
              }
            />
            <Route path="supplier-activities" element={<SupplierActivities />} />
            <Route path="event-category" element={<EventCategory />} />
            <Route path="activity-category" element={<ActivityCategory />} />
            <Route path="attraction-category" element={<AttractionCategory />} />
            <Route path="language" element={<Language />} />
            <Route path="inclusions" element={<Inclusions />} />
            <Route path="suitable" element={<Suitable />} />
            <Route path="settings" element={<Settings />} />
            <Route path="transactions" element={<Transactions />} />
          </Route>

          {/* Catch all route - 404 */}
          <Route
            path="*"
            element={
              <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                  <h1 className="text-4xl font-bold text-gray-800">404</h1>
                  <p className="text-gray-600 mt-2">Page not found</p>
                  <Link
                    to="/"
                    className="mt-4 inline-block px-4 py-2 bg-pink-600 text-white rounded-md"
                  >
                    Go to Dashboard
                  </Link>
                </div>
              </div>
            }
          />
        </Routes>
    </QueryProvider>
  );
}

export default App;
